// Scene: renderer setup, terrain + world views, unit instancing with snapshot
// interpolation, tile/building/unit picking, drag-box selection support,
// placement ghost, client-side validity caches, combat FX, sim-fed fog.

import * as THREE from 'three';
import {
  MAX_ENTITIES,
  TICK_MS,
  Terrain,
  UnitKind,
  WELL_MAX_ELEVATION,
  isWalkable,
} from '../shared/constants';
import { UNIT_STRIDE, BUILDING_STRIDE, type WorkerToMain } from '../shared/protocol';
import { RtsCamera } from './camera';
import { buildTerrain, type TerrainBuild } from './terrain';
import {
  BUILDING_DEPOSIT,
  BUILDING_NEEDS_GROUNDWATER,
  BUILDING_SIZES,
  BarsView,
  BouldersView,
  BuildingsView,
  DepositsView,
  FlowView,
  FogView,
  RoadsView,
  ShotsView,
  TerritoryView,
} from './views';

type MapMsg = Extract<WorkerToMain, { type: 'map' }>;
type SnapshotMsg = Extract<WorkerToMain, { type: 'snapshot' }>;
type EventsMsg = Extract<WorkerToMain, { type: 'events' }>;

interface Snap {
  time: number;
  tick: number;
  speed: number;
  units: Float32Array;
  count: number;
}

/** per-kind unit visuals: [geometry factory, base color] */
function unitGeoFor(kind: number): THREE.BufferGeometry {
  switch (kind) {
    case UnitKind.Scout: {
      const g = new THREE.ConeGeometry(0.18, 0.62, 5);
      g.translate(0, 0.31, 0);
      return g;
    }
    case UnitKind.Shieldbearer: {
      const g = new THREE.CylinderGeometry(0.3, 0.34, 0.62, 8);
      g.translate(0, 0.31, 0);
      return g;
    }
    case UnitKind.Axeman: {
      const g = new THREE.BoxGeometry(0.4, 0.62, 0.34);
      g.translate(0, 0.31, 0);
      return g;
    }
    case UnitKind.Archer: {
      const g = new THREE.ConeGeometry(0.24, 0.72, 6);
      g.translate(0, 0.36, 0);
      return g;
    }
    case UnitKind.Arbalist: {
      const g = new THREE.CylinderGeometry(0.12, 0.28, 0.72, 6);
      g.translate(0, 0.36, 0);
      return g;
    }
    case UnitKind.Sapper: {
      const g = new THREE.SphereGeometry(0.28, 8, 6);
      g.translate(0, 0.28, 0);
      return g;
    }
    default: {
      const g = new THREE.BoxGeometry(0.38, 0.56, 0.38);
      g.translate(0, 0.28, 0);
      return g;
    }
  }
}

const UNIT_COLORS: Record<number, string> = {
  [UnitKind.Scout]: '#7fe0a0',
  [UnitKind.Shieldbearer]: '#c8d0e0',
  [UnitKind.Axeman]: '#d08050',
  [UnitKind.Archer]: '#a0c860',
  [UnitKind.Arbalist]: '#708050',
  [UnitKind.Sapper]: '#e0b050',
  [UnitKind.Bandit]: '#8a4040',
};

const MILITARY_KINDS = [
  UnitKind.Scout,
  UnitKind.Shieldbearer,
  UnitKind.Axeman,
  UnitKind.Archer,
  UnitKind.Arbalist,
  UnitKind.Sapper,
  UnitKind.Bandit,
];

export class GameScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  cam!: RtsCamera;
  private terrain: TerrainBuild | null = null;
  private porters: THREE.InstancedMesh;
  private donkeys: THREE.InstancedMesh;
  private surveyors: THREE.InstancedMesh;
  private military = new Map<number, THREE.InstancedMesh>();
  private bars: BarsView;
  private shots: ShotsView;
  private selectionRings: THREE.InstancedMesh;
  private rallyMarker: THREE.Mesh;
  private highlight: THREE.Mesh;
  private ghost: THREE.Mesh;
  private ghostMat: THREE.MeshBasicMaterial;
  private raycaster = new THREE.Raycaster();
  private prevSnap: Snap | null = null;
  private currSnap: Snap | null = null;
  private lastBuildings: Float32Array | null = null;
  private lastBuildingCount = 0;
  hoveredTile: { x: number; y: number } | null = null;
  /** unit ids selected on the main thread; rings rendered here */
  selectedUnits = new Set<number>();
  /** interpolated screen-space unit positions from the last frame (picking) */
  private unitScreen = new Map<number, { x: number; y: number; kind: number; owner: number }>();

  // world views
  private buildingsView: BuildingsView | null = null;
  private roadsView: RoadsView | null = null;
  private bouldersView: BouldersView | null = null;
  private territoryView: TerritoryView | null = null;
  private depositsView: DepositsView | null = null;
  private flowView: FlowView | null = null;
  private fogView: FogView | null = null;

  // client-side caches (fed by map + events; used for ghost validity)
  mapW = 0;
  mapH = 0;
  terrainData: Uint8Array | null = null;
  elevationData: Uint8Array | null = null;
  treeDensity: Uint8Array | null = null;
  stoneAmount: Uint8Array | null = null;
  roadsData: Uint8Array | null = null;
  hexOfTile: Int32Array | null = null;
  hexOwner: Int8Array | null = null;
  hexSurveyed: Uint8Array | null = null;
  depositsData: Int32Array | null = null;
  fogData: Uint8Array | null = null;
  private blockedData: Uint8Array | null = null;
  private doorTiles = new Set<number>();
  private treesDirty = false;
  private treesRebuildAt = 0;
  private centerOnHq = false;

  constructor(private canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.scene.background = new THREE.Color('#101418');

    const sun = new THREE.DirectionalLight('#fff6e0', 2.2);
    sun.position.set(1, 2, 0.7);
    this.scene.add(sun, new THREE.AmbientLight('#8899bb', 0.9));

    this.porters = new THREE.InstancedMesh(
      new THREE.SphereGeometry(0.22, 10, 8),
      new THREE.MeshLambertMaterial({ color: '#ffd34d' }),
      MAX_ENTITIES,
    );
    this.donkeys = new THREE.InstancedMesh(
      new THREE.CapsuleGeometry(0.24, 0.3, 4, 8),
      new THREE.MeshLambertMaterial({ color: '#a4763f' }),
      1024,
    );
    const surveyorGeo = new THREE.ConeGeometry(0.24, 0.7, 6);
    surveyorGeo.translate(0, 0.35, 0);
    this.surveyors = new THREE.InstancedMesh(
      surveyorGeo,
      new THREE.MeshLambertMaterial({ color: '#4dd6e8' }),
      1024,
    );
    for (const m of [this.porters, this.donkeys, this.surveyors]) {
      m.count = 0;
      m.frustumCulled = false;
      this.scene.add(m);
    }
    // one instanced mesh per military kind; per-instance color (veteran gold)
    for (const kind of MILITARY_KINDS) {
      const mesh = new THREE.InstancedMesh(
        unitGeoFor(kind),
        new THREE.MeshLambertMaterial({ color: '#ffffff' }),
        1024,
      );
      mesh.count = 0;
      mesh.frustumCulled = false;
      this.military.set(kind, mesh);
      this.scene.add(mesh);
    }

    this.bars = new BarsView();
    this.scene.add(this.bars.back, this.bars.front);
    this.shots = new ShotsView();
    this.scene.add(this.shots.line);

    const ringGeo = new THREE.RingGeometry(0.34, 0.46, 16);
    ringGeo.rotateX(-Math.PI / 2);
    this.selectionRings = new THREE.InstancedMesh(
      ringGeo,
      new THREE.MeshBasicMaterial({
        color: '#7fff9f',
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
      }),
      512,
    );
    this.selectionRings.count = 0;
    this.selectionRings.frustumCulled = false;
    this.selectionRings.renderOrder = 300;
    this.scene.add(this.selectionRings);

    const rallyGeo = new THREE.ConeGeometry(0.3, 1.0, 5);
    rallyGeo.translate(0, 0.5, 0);
    this.rallyMarker = new THREE.Mesh(
      rallyGeo,
      new THREE.MeshBasicMaterial({ color: '#ffe28a', transparent: true, opacity: 0.9 }),
    );
    this.rallyMarker.visible = false;
    this.scene.add(this.rallyMarker);

    const hlGeo = new THREE.PlaneGeometry(1, 1);
    const hlMat = new THREE.MeshBasicMaterial({
      color: '#ffffff',
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
    });
    this.highlight = new THREE.Mesh(hlGeo, hlMat);
    this.highlight.rotation.x = -Math.PI / 2;
    this.highlight.visible = false;
    this.scene.add(this.highlight);

    this.ghostMat = new THREE.MeshBasicMaterial({
      color: '#44dd66',
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
    });
    this.ghost = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), this.ghostMat);
    this.ghost.visible = false;
    this.scene.add(this.ghost);
  }

  setMap(map: MapMsg): void {
    // dispose previous world
    if (this.terrain) {
      this.scene.remove(this.terrain.group, this.terrain.waterMesh, this.terrain.trees);
    }
    if (this.buildingsView) this.scene.remove(this.buildingsView.group);
    if (this.roadsView) this.scene.remove(this.roadsView.group);
    if (this.bouldersView) this.scene.remove(this.bouldersView.mesh);
    if (this.territoryView) this.scene.remove(this.territoryView.group);
    if (this.depositsView) this.scene.remove(this.depositsView.mesh);
    if (this.flowView) this.scene.remove(this.flowView.line);
    if (this.fogView) this.scene.remove(this.fogView.mesh);

    this.mapW = map.info.width;
    this.mapH = map.info.height;
    this.terrainData = map.terrain;
    this.elevationData = map.elevation;
    this.treeDensity = map.treeDensity;
    this.stoneAmount = map.stoneAmount;
    this.roadsData = map.roads;
    this.hexOfTile = map.hexOfTile;
    this.hexOwner = map.hexOwner;
    this.hexSurveyed = map.hexSurveyed;
    this.depositsData = map.deposits;
    this.fogData = map.fog;
    this.blockedData = new Uint8Array(this.mapW * this.mapH);

    this.terrain = buildTerrain(map);
    this.scene.add(this.terrain.group, this.terrain.waterMesh, this.terrain.trees);

    const h = this.terrain.heightAt;
    this.buildingsView = new BuildingsView(h);
    this.roadsView = new RoadsView(this.mapW, h);
    this.bouldersView = new BouldersView(this.mapW, h, map.stoneAmount);
    this.territoryView = new TerritoryView(this.mapW, this.mapH, map.hexOfTile, h);
    this.territoryView.rebuild(map.hexOwner);
    this.depositsView = new DepositsView(map.info.hexCols, map.info.hexRows, h);
    this.depositsView.rebuild(map.deposits, map.hexSurveyed);
    this.flowView = new FlowView(this.mapW);
    this.fogView = new FogView(this.mapW, this.mapH);
    this.scene.add(
      this.buildingsView.group,
      this.roadsView.group,
      this.bouldersView.mesh,
      this.territoryView.group,
      this.depositsView.mesh,
      this.flowView.line,
      this.fogView.mesh,
    );
    const roadTiles: number[] = [];
    for (let i = 0; i < map.roads.length; i++) if (map.roads[i]) roadTiles.push(i);
    this.roadsView.addTiles(roadTiles);
    this.fogView.setFog(map.fog);

    this.cam = new RtsCamera(
      this.canvas.clientWidth / Math.max(1, this.canvas.clientHeight),
      this.mapW,
      this.mapH,
    );
    this.cam.attach(this.canvas);
    this.prevSnap = null;
    this.currSnap = null;
    this.lastBuildings = null;
    this.lastBuildingCount = 0;
    this.selectedUnits.clear();
    this.centerOnHq = true;
  }

  onSnapshot(msg: SnapshotMsg): void {
    this.prevSnap = this.currSnap;
    this.currSnap = {
      time: performance.now(),
      tick: msg.tick,
      speed: msg.speed,
      units: msg.units,
      count: msg.unitCount,
    };
    this.buildingsView?.update(msg.buildings, msg.buildingCount);
    this.lastBuildings = msg.buildings;
    this.lastBuildingCount = msg.buildingCount;
    // first snapshot after a map load: center the camera on the HQ
    if (this.centerOnHq && msg.buildingCount > 0 && this.cam) {
      this.centerOnHq = false;
      const size = msg.buildings[4];
      this.cam.target.set(msg.buildings[2] + size / 2, 0, msg.buildings[3] + size / 2);
    }
    // rebuild blocked + door caches from building footprints
    if (this.blockedData) {
      this.blockedData.fill(0);
      this.doorTiles.clear();
      for (let i = 0; i < msg.buildingCount; i++) {
        const o = i * BUILDING_STRIDE;
        const x = msg.buildings[o + 2];
        const y = msg.buildings[o + 3];
        const size = msg.buildings[o + 4];
        for (let ty = y; ty < y + size; ty++) {
          for (let tx = x; tx < x + size; tx++) {
            this.blockedData[ty * this.mapW + tx] = 1;
          }
        }
        this.doorTiles.add((y + size) * this.mapW + (x + (size >> 1)));
      }
    }
  }

  onEvents(msg: EventsMsg): void {
    const t = msg.tilesChanged;
    for (let k = 0; k < t.length; k += 4) {
      const i = t[k];
      if (this.terrainData) this.terrainData[i] = t[k + 1];
      if (this.treeDensity) this.treeDensity[i] = t[k + 2];
      if (this.stoneAmount) this.stoneAmount[i] = t[k + 3];
      this.treesDirty = true;
    }
    if (msg.roadsAdded.length > 0 && this.roadsData && this.roadsView) {
      for (let k = 0; k < msg.roadsAdded.length; k++) this.roadsData[msg.roadsAdded[k]] = 1;
      this.roadsView.addTiles(msg.roadsAdded);
    }
    if (msg.territory && this.territoryView) {
      this.hexOwner = msg.territory;
      this.territoryView.rebuild(msg.territory);
    }
    if (msg.surveyed) this.hexSurveyed = msg.surveyed;
    if (msg.deposits) this.depositsData = msg.deposits;
    if (
      (msg.surveyed || msg.deposits) &&
      this.depositsView &&
      this.depositsData &&
      this.hexSurveyed
    ) {
      this.depositsView.rebuild(this.depositsData, this.hexSurveyed);
    }
    if (msg.fog && this.fogView) {
      this.fogData = msg.fog;
      this.fogView.setFog(msg.fog);
    }
    if (msg.shots.length > 0) this.shots.addShots(msg.shots, performance.now());
  }

  setSelectedBuilding(id: number): void {
    this.buildingsView?.setSelected(id);
  }

  toggleFlow(): boolean {
    if (!this.flowView) return false;
    this.flowView.enabled = !this.flowView.enabled;
    return this.flowView.enabled;
  }

  private updateUnits(now: number): void {
    const curr = this.currSnap;
    if (!curr || !this.terrain) return;
    const prev = this.prevSnap;
    const tickWallMs = TICK_MS / Math.max(1, curr.speed);
    const alpha = prev ? Math.min(1, (now - curr.time) / tickWallMs) : 1;

    const prevById = new Map<number, number>();
    if (prev) {
      for (let i = 0; i < prev.count; i++)
        prevById.set(prev.units[i * UNIT_STRIDE], i * UNIT_STRIDE);
    }
    const m = new THREE.Matrix4();
    const color = new THREE.Color();
    const gold = new THREE.Color('#ffd700');
    let np = 0;
    let nd = 0;
    let ns = 0;
    const counts = new Map<number, number>();
    for (const kind of this.military.keys()) counts.set(kind, 0);
    let nr = 0;
    this.bars.begin();
    this.unitScreen.clear();
    const w2 = this.canvas.clientWidth / 2;
    const h2 = this.canvas.clientHeight / 2;
    const v = new THREE.Vector3();

    for (let i = 0; i < curr.count; i++) {
      const o = i * UNIT_STRIDE;
      const id = curr.units[o];
      const kind = curr.units[o + 1];
      const donkey = curr.units[o + 6] > 0;
      const hpPct = curr.units[o + 8];
      const veteran = curr.units[o + 9] > 0;
      const owner = curr.units[o + 7];
      let x = curr.units[o + 2];
      let y = curr.units[o + 3];
      const po = prev ? prevById.get(id) : undefined;
      if (po !== undefined && prev) {
        const px = prev.units[po + 2];
        const py = prev.units[po + 3];
        x = px + (x - px) * alpha;
        y = py + (y - py) * alpha;
      }
      const gy = this.terrain.heightAt(Math.round(x), Math.round(y)) + 0.22;
      m.makeTranslation(x, gy, y);
      const mil = this.military.get(kind);
      if (mil) {
        const n = counts.get(kind)!;
        if (n < mil.instanceMatrix.count) {
          mil.setMatrixAt(n, m);
          color.set(UNIT_COLORS[kind] ?? '#cccccc');
          if (veteran) color.lerp(gold, 0.5);
          mil.setColorAt(n, color);
          counts.set(kind, n + 1);
        }
        if (hpPct < 100) this.bars.push(x, gy + 0.95, y, hpPct);
        // selection ring + screen-space cache (military are the selectable units)
        if (this.selectedUnits.has(id) && nr < this.selectionRings.instanceMatrix.count) {
          const rm = new THREE.Matrix4().makeTranslation(x, gy + 0.02, y);
          this.selectionRings.setMatrixAt(nr++, rm);
        }
        if (this.cam) {
          v.set(x, gy, y).project(this.cam.camera);
          this.unitScreen.set(id, { x: (v.x + 1) * w2, y: (1 - v.y) * h2, kind, owner });
        }
      } else if (kind === UnitKind.Porter) {
        if (donkey) this.donkeys.setMatrixAt(nd++, m);
        else this.porters.setMatrixAt(np++, m);
        if (hpPct < 100) this.bars.push(x, gy + 0.7, y, hpPct, 0.7);
      } else {
        this.surveyors.setMatrixAt(ns++, m);
        if (hpPct < 100) this.bars.push(x, gy + 0.95, y, hpPct, 0.7);
      }
    }
    this.porters.count = np;
    this.porters.instanceMatrix.needsUpdate = true;
    this.donkeys.count = nd;
    this.donkeys.instanceMatrix.needsUpdate = true;
    this.surveyors.count = ns;
    this.surveyors.instanceMatrix.needsUpdate = true;
    for (const [kind, mesh] of this.military) {
      mesh.count = counts.get(kind) ?? 0;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    this.selectionRings.count = nr;
    this.selectionRings.instanceMatrix.needsUpdate = true;

    // building health bars (damaged only)
    if (this.lastBuildings) {
      for (let i = 0; i < this.lastBuildingCount; i++) {
        const o = i * BUILDING_STRIDE;
        const hpPct = this.lastBuildings[o + 11];
        if (hpPct >= 100) continue;
        const x = this.lastBuildings[o + 2];
        const y = this.lastBuildings[o + 3];
        const size = this.lastBuildings[o + 4];
        const cx = x + size / 2;
        const cz = y + size / 2;
        const gy = this.terrain.heightAt(Math.round(cx), Math.round(cz));
        this.bars.push(cx, gy + 2.0, cz, hpPct, size);
      }
    }
    this.bars.end();

    this.flowView?.update(curr.units, curr.count, this.terrain.heightAt);
  }

  /** nearest selectable (player military) unit within pixel radius; -1 if none */
  pickUnit(clientX: number, clientY: number, radiusPx = 14): number {
    const rect = this.canvas.getBoundingClientRect();
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    let best = -1;
    let bestD = radiusPx * radiusPx;
    for (const [id, p] of this.unitScreen) {
      if (p.owner !== 0) continue;
      const dx = p.x - sx;
      const dy = p.y - sy;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = id;
      }
    }
    return best;
  }

  /** player military unit ids inside a screen rectangle (drag-box selection) */
  unitsInRect(x0: number, y0: number, x1: number, y1: number): number[] {
    const rect = this.canvas.getBoundingClientRect();
    const minX = Math.min(x0, x1) - rect.left;
    const maxX = Math.max(x0, x1) - rect.left;
    const minY = Math.min(y0, y1) - rect.top;
    const maxY = Math.max(y0, y1) - rect.top;
    const out: number[] = [];
    for (const [id, p] of this.unitScreen) {
      if (p.owner !== 0) continue;
      if (p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY) out.push(id);
    }
    return out.sort((a, b) => a - b);
  }

  setRallyMarker(tile: number): void {
    if (tile < 0 || !this.terrain) {
      this.rallyMarker.visible = false;
      return;
    }
    const x = (tile % this.mapW) + 0.5;
    const y = Math.floor(tile / this.mapW) + 0.5;
    this.rallyMarker.visible = true;
    this.rallyMarker.position.set(x, this.terrain.heightAt(Math.round(x), Math.round(y)), y);
  }

  /** kind counts for a set of unit ids (from the latest rendered snapshot);
   *  also prunes ids that no longer exist (dead or garrisoned) */
  kindsOf(ids: Set<number>): Map<number, number> {
    const out = new Map<number, number>();
    for (const id of [...ids]) {
      const p = this.unitScreen.get(id);
      if (!p) {
        ids.delete(id);
        continue;
      }
      out.set(p.kind, (out.get(p.kind) ?? 0) + 1);
    }
    return out;
  }

  /** latest unit snapshot (read-only; minimap/tutorial) */
  get unitsSnapshot(): { units: Float32Array; count: number } | null {
    return this.currSnap ? { units: this.currSnap.units, count: this.currSnap.count } : null;
  }

  /** latest building snapshot (read-only; minimap/tutorial) */
  get buildingsSnapshot(): { buildings: Float32Array; count: number } | null {
    return this.lastBuildings
      ? { buildings: this.lastBuildings, count: this.lastBuildingCount }
      : null;
  }

  /** building info from the latest snapshot (defIdx, owner) or null */
  buildingInfo(id: number): { defIdx: number; owner: number } | null {
    if (!this.lastBuildings) return null;
    for (let i = 0; i < this.lastBuildingCount; i++) {
      const o = i * BUILDING_STRIDE;
      if (this.lastBuildings[o] === id) {
        return { defIdx: this.lastBuildings[o + 1], owner: this.lastBuildings[o + 10] };
      }
    }
    return null;
  }

  /** client-side approximation of sim placement rules (sim revalidates) */
  placementValid(x: number, y: number, defIdx: number): boolean {
    const size = BUILDING_SIZES[defIdx] ?? 2;
    if (
      !this.terrainData ||
      !this.elevationData ||
      !this.stoneAmount ||
      !this.roadsData ||
      !this.blockedData ||
      !this.hexOfTile ||
      !this.hexOwner ||
      !this.hexSurveyed ||
      !this.depositsData
    )
      return false;
    if (x < 0 || y < 0 || x + size > this.mapW || y + size + 1 > this.mapH) return false;
    const depositKind = BUILDING_DEPOSIT[defIdx] ?? 0;
    const groundTerrain = depositKind > 0 ? Terrain.Rock : Terrain.Grass;
    for (let ty = y; ty < y + size; ty++) {
      for (let tx = x; tx < x + size; tx++) {
        const i = ty * this.mapW + tx;
        if (this.terrainData[i] !== groundTerrain) return false;
        if (depositKind === 0 && this.stoneAmount[i] > 0) return false;
        if (this.roadsData[i] || this.blockedData[i]) return false;
        if (this.doorTiles.has(i)) return false; // never cover a building's door
        if (this.hexOwner[this.hexOfTile[i]] !== 0) return false;
        if (BUILDING_NEEDS_GROUNDWATER[defIdx] && this.elevationData[i] >= WELL_MAX_ELEVATION)
          return false;
      }
    }
    if (depositKind > 0) {
      const half = size >> 1;
      const hex = this.hexOfTile[(y + half) * this.mapW + (x + half)];
      if (!this.hexSurveyed[hex]) return false;
      if (this.depositsData[hex] >>> 24 !== depositKind) return false;
      if ((this.depositsData[hex] & 0xffffff) <= 0) return false;
    }
    const door = (y + size) * this.mapW + (x + (size >> 1));
    if (!isWalkable(this.terrainData[door] as Terrain) || this.blockedData[door]) return false;
    return true;
  }

  showGhost(x: number, y: number, defIdx: number): void {
    if (!this.terrain) return;
    const size = BUILDING_SIZES[defIdx] ?? 2;
    const valid = this.placementValid(x, y, defIdx);
    this.ghost.visible = true;
    this.ghost.scale.set(size * 0.95, 1.2, size * 0.95);
    const cx = x + size / 2;
    const cz = y + size / 2;
    this.ghost.position.set(cx, this.terrain.heightAt(Math.round(cx), Math.round(cz)) + 0.6, cz);
    this.ghostMat.color.set(valid ? '#44dd66' : '#e05545');
  }

  hideGhost(): void {
    this.ghost.visible = false;
  }

  pick(clientX: number, clientY: number): { x: number; y: number } | null {
    if (!this.terrain || !this.cam) return null;
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.cam.camera);
    const hits = this.raycaster.intersectObjects(this.terrain.group.children, false);
    const hit = hits[0];
    if (!hit) {
      this.hoveredTile = null;
      this.highlight.visible = false;
      return null;
    }
    const tx = Math.floor(hit.point.x);
    const ty = Math.floor(hit.point.z);
    this.hoveredTile = { x: tx, y: ty };
    this.highlight.visible = true;
    this.highlight.position.set(tx + 0.5, this.terrain.heightAt(tx, ty) + 0.03, ty + 0.5);
    return this.hoveredTile;
  }

  /** raycast building selection; returns building id or -1 */
  pickBuilding(clientX: number, clientY: number): number {
    if (!this.buildingsView || !this.cam) return -1;
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.cam.camera);
    const hits = this.raycaster.intersectObjects(this.buildingsView.pickables, false);
    const hit = hits[0];
    return hit ? ((hit.object.userData.buildingId as number) ?? -1) : -1;
  }

  render(now: number, dt: number): void {
    if (!this.cam) return;
    this.cam.update(dt);
    this.updateUnits(now);
    this.shots.update(now, this.terrain?.heightAt ?? (() => 0));

    // throttled tree rebuild after harvest events
    if (this.treesDirty && now > this.treesRebuildAt && this.terrain && this.treeDensity) {
      this.treesDirty = false;
      this.treesRebuildAt = now + 1000;
      this.scene.remove(this.terrain.trees);
      this.terrain.rebuildTrees(this.treeDensity);
      this.scene.add(this.terrain.trees);
      this.bouldersView?.rebuild(this.stoneAmount!);
    }

    this.renderer.render(this.scene, this.cam.camera);
  }

  resize(): void {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    this.renderer.setSize(w, h, false);
    this.cam?.resize(w / Math.max(1, h));
  }
}
